import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import type {
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import {
  isManualWorkflowTerminateEligibleStatus,
  type ManualImpactAssessmentGridRow,
  type TriageImpactedItem,
} from "../../services/manualAssessmentService";
import {
  getAiImpactDownloadReportTooltip,
  getAiImpactRerunTooltip,
  getAiImpactStartManualAssessmentTooltip,
  getAiImpactViewReportTooltip,
} from "./aiImpactAssessmentActionTooltips";
import { ManualAssessmentProcessNameCell } from "../../utils/manualAssessmentProcessNameLink";
import { getAiImpactAssessmentActionsVisibility } from "../../utils/aiImpactAssessmentActionsVisibility";
import {
  getAiAssessmentStatusPillPresentation,
  resolveAssessmentStatusForPill,
} from "../../utils/assessmentHistoryStatus";
import eyeIcon from "../../assets/eye.svg";
import downloadIcon from "../../assets/download.svg";
import gitBranchIcon from "../../assets/git-branch.svg";
import retryIcon from "../../assets/retry.svg";
import stopIcon from "../../assets/icon-stop.svg";
import shareIcon from "../../assets/share.svg";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../constants/dialogZIndex";
import {
  NAV_SOURCE_OMIT_DOMAIN,
  buildRegulationDetailHref,
} from "../../utils/externalNavigation";
import { AiImpactedConceptsCell } from "./ImpactedConceptsChipCell";

export { AiImpactedConceptsCell } from "./ImpactedConceptsChipCell";

const AI_STATUS_PILL_FONT = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: "12px",
  textAlign: "center" as const,
} as const;

function normalizeAiAssessmentStatus(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.trim().toLowerCase().replace(/\s+/g, "_");
}

function coerceStatusLabelFallback(raw: unknown): string {
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }
  return "";
}

export function getAiAssessmentCenterStatusHoverTitle(
  rawStatus: unknown,
  failedConceptNames?: readonly string[] | null,
): string | undefined {
  const raw =
    typeof rawStatus === "string" ? rawStatus.trim().toLowerCase() : "";
  const normalized = raw.replace(/\s+/g, "_");

  if (normalized === "failed" || normalized === "failure") {
    return "This assessment did not complete successfully due to an unexpected processing issue. Please try again later.";
  }

  if (normalized === "partial") {
    const names = (failedConceptNames ?? []).filter(
      (n) => typeof n === "string" && n.trim().length > 0,
    );
    if (names.length > 0) {
      return `Some assessment concepts (${names.join(", ")}) could not be processed, so only partial results are available.`;
    }
    return "Some assessment concepts could not be processed, so only partial results are available.";
  }

  return undefined;
}

/** Same hover copy as AI table / Results modal; resolves raw + formatted status like the pill. */
export function resolveAiAssessmentCenterStatusHoverTitle(
  rawStatus: unknown,
  formattedStatus?: unknown,
  failedConceptNames?: readonly string[] | null,
): string | undefined {
  const statusKey = resolveAssessmentStatusForPill(rawStatus, formattedStatus);
  return getAiAssessmentCenterStatusHoverTitle(statusKey, failedConceptNames);
}

function AiAssessmentStatusPill({
  label,
  backgroundColor,
  color,
  showInfoIcon,
}: Readonly<{
  label: string;
  backgroundColor: string;
  color: string;
  showInfoIcon: boolean;
}>) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: showInfoIcon ? "4px" : 0,
        boxSizing: "border-box",
        minWidth: 88,
        height: 24,
        px: "10px",
        py: "4px",
        borderRadius: "9999px",
        backgroundColor,
        color,
        ...AI_STATUS_PILL_FONT,
      }}
    >
      <Box component="span" sx={{ color: "inherit" }}>
        {label}
      </Box>
      {showInfoIcon ? (
        <InfoOutlinedIcon
          sx={{
            fontSize: 12,
            color: "inherit",
            flexShrink: 0,
          }}
          aria-hidden
        />
      ) : null}
    </Box>
  );
}

export function AiStyleAssessmentStatusCell({
  rawStatus,
  tooltipTitle,
  elevatedDialogTooltips = false,
  contentAlign = "center",
}: Readonly<{
  rawStatus: unknown;
  tooltipTitle?: string | null;
  /** Raise tooltip above nested dialogs (AI Results modal). */
  elevatedDialogTooltips?: boolean;
  /** Vertical alignment within a tall grid cell. */
  contentAlign?: "center" | "top";
}>) {
  const pill = getAiAssessmentStatusPillPresentation(rawStatus);
  if (!pill) {
    return <span>-</span>;
  }

  const statusPill = (
    <AiAssessmentStatusPill
      label={pill.label}
      backgroundColor={pill.backgroundColor}
      color={pill.color}
      showInfoIcon={pill.showInfoIcon}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: contentAlign === "top" ? "flex-start" : "center",
        height: contentAlign === "top" ? "auto" : "100%",
      }}
    >
      {tooltipTitle?.trim() ? (
        <Tooltip
          title={tooltipTitle}
          arrow
          placement="top"
          slotProps={{
            ...(elevatedDialogTooltips ? IMPACT_ACTION_TOOLTIP_SLOT_PROPS : {}),
            tooltip: {
              sx: {
                maxWidth: 360,
                whiteSpace: "normal",
              },
            },
          }}
        >
          <Box component="span" sx={{ display: "inline-flex", cursor: "default" }}>
            {statusPill}
          </Box>
        </Tooltip>
      ) : (
        statusPill
      )}
    </Box>
  );
}

function isManualStatusNotStarted(normalized: string): boolean {
  return normalized === "not_started" || normalized === "init";
}

function isManualStatusInProgress(normalized: string): boolean {
  return (
    normalized === "in_progress" ||
    normalized === "inprogress" ||
    normalized === "running"
  );
}

function getManualStatusPillStyles(normalized: string): {
  backgroundColor: string;
  color: string;
  showInfoIcon: boolean;
} {
  if (normalized === "terminated") {
    return {
      backgroundColor: "#F2F2F2",
      color: "#8C8C8C",
      showInfoIcon: false,
    };
  }
  if (isManualStatusNotStarted(normalized)) {
    return {
      backgroundColor: "#FFF0B3",
      color: "#FF8B00",

      showInfoIcon: false,
    };
  }
  if (isManualStatusInProgress(normalized)) {
    return {
      backgroundColor: "#E7ECFC",
      color: "#295EF1",
      showInfoIcon: false,
    };
  }
  if (normalized === "completed" || normalized === "complete") {
    return {
      backgroundColor: "#E3F7F0",
      color: "#007B4F",
      showInfoIcon: false,
    };
  }
  return {
    backgroundColor: "#F5F5F5",
    color: "#616161",
    showInfoIcon: false,
  };
}

function formatManualStatusLabel(normalized: string, rawDisplay: string): string {
  if (normalized === "terminated") {
    return "Terminated";
  }
  if (isManualStatusNotStarted(normalized)) {
    return "Not Started";
  }
  if (isManualStatusInProgress(normalized)) {
    return "In Progress";
  }
  if (normalized === "completed" || normalized === "complete") {
    return "Completed";
  }
  return rawDisplay?.trim() || "—";
}

function ManualStyleAssessmentStatusCell({
  rawStatus,
}: Readonly<{ rawStatus: unknown }>) {
  const raw = rawStatus;
  const normalized = normalizeAiAssessmentStatus(raw);
  const label = formatManualStatusLabel(
    normalized,
    coerceStatusLabelFallback(raw),
  );
  const { backgroundColor, color, showInfoIcon } =
    getManualStatusPillStyles(normalized);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
          minWidth: 88,
          height: 24,
          px: "10px",
          py: "4px",
          borderRadius: "9999px",
          backgroundColor,
          color,
          ...AI_STATUS_PILL_FONT,
        }}
      >
        {label}
        {showInfoIcon ? (
          <>
            {" "}
            <InfoOutlinedIcon
              sx={{
                fontSize: 12,
                ml: "2px",
                color: "inherit",
                verticalAlign: "middle",
              }}
              aria-hidden
            />
          </>
        ) : null}
      </Box>
    </Box>
  );
}

const IMPACT_LEVEL_PILL_FONT = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: "16px",
  textAlign: "center" as const,
  textTransform: "capitalize" as const,
} as const;

function coerceImpactLevelText(rawDisplay: unknown): string {
  if (typeof rawDisplay === "string") return rawDisplay.trim();
  if (typeof rawDisplay === "number" && Number.isFinite(rawDisplay)) {
    return String(rawDisplay);
  }
  return "";
}

function getImpactLevelPillStyles(rawDisplay: unknown): {
  backgroundColor: string;
  color: string;
  label: string;
} {
  const trimmed = coerceImpactLevelText(rawDisplay);

  if (!trimmed || trimmed === "—") {
    return { backgroundColor: "#F5F5F5", color: "#616161", label: "—" };
  }

  const normalized = trimmed.toLowerCase();

  if (normalized === "high") {
    return { backgroundColor: "#FFE3E3", color: "#AA0000", label: "High" };
  }
  if (normalized === "medium") {
    return { backgroundColor: "#FFE3CD", color: "#E94A00", label: "Medium" };
  }
  if (normalized === "low") {
    return { backgroundColor: "#FFF0B3", color: "#FF8B00", label: "Low" };
  }
  if (normalized === "no" || normalized === "none") {
    return { backgroundColor: "#E3F7F0", color: "#007B4F", label: "No" };
  }

  return {
    backgroundColor: "#F5F5F5",
    color: "#616161",
    label: trimmed,
  };
}

/** Table display for `by` — row data stays `user` / `system` for API and actions. */
export function formatAiAssessmentTypeForDisplay(raw: unknown): string {
  if (raw == null) return "—";
  if (typeof raw !== "string") return "—";
  const s = raw.trim();
  if (!s || s === "—") return "—";
  const n = s.toLowerCase();
  if (n === "user") return "Custom Scope";
  if (n === "system") return "Company Scope";
  return s;
}

function isPlaceholderGridValue(value: string): boolean {
  const trimmed = value.trim();
  return !trimmed || trimmed === "—" || trimmed === "-";
}

/** Regulation detail href for Assessment Center rows (shell URL in iframe, in-app path otherwise). */
export function buildAssessmentCenterRegulationDetailHref(
  regulationId: string,
  version: string,
): string | null {
  const id = regulationId.trim();
  if (isPlaceholderGridValue(id)) return null;

  const versionTrimmed = version.trim();
  return buildRegulationDetailHref(id, {
    ...(isPlaceholderGridValue(versionTrimmed)
      ? {}
      : { version: versionTrimmed }),
    source: NAV_SOURCE_OMIT_DOMAIN.ASSESSMENT_CENTER,
  });
}

const REGULATION_ID_LINK_SX = {
  color: "#295ef1",
  fontWeight: 500,
  cursor: "pointer",
  fontSize: "14px",
} as const;

function RegulationIdLinkCell({
  regulationId,
  version,
}: Readonly<{
  regulationId: string;
  version: string;
}>) {
  const href = buildAssessmentCenterRegulationDetailHref(regulationId, version);
  const label = regulationId.trim() || "—";

  if (!href) {
    return <>{label}</>;
  }

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={REGULATION_ID_LINK_SX}
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </Link>
  );
}

export type AiImpactAssessmentRow = Readonly<{
  /** Raw API status (bundle task) — actions + filters use this. */
  rawStatus: string;
  /** Formatted for display (Assessment History parity). */
  status: string;
  taskUuid: string;
  regulationId: string;
  regulation?: string;
  version: string;
  assessmentType: string;
  reportUrl?: string | null;
  /** From API `failed_concept_names` — partial status hover text. */
  failedConceptNames?: readonly string[];
  /** IA `impacted_items` for manual triage (`POST /manual-assessment/triage-processes-recommendation`). */
  impactedItems?: readonly TriageImpactedItem[];
}>;

export type AiImpactAssessmentActionHandlers = Readonly<{
  onViewReport?: (row: AiImpactAssessmentRow) => void;
  onDownloadReport?: (row: AiImpactAssessmentRow) => void;
  onStartManualAssessment?: (row: AiImpactAssessmentRow) => void;
  onRetry?: (row: AiImpactAssessmentRow) => void;
  /** When false, system-type rows do not show Re-run (requires `gri.assessment.impact.system.execute`). */
  canRerunSystem?: boolean;
  downloadingFilePath?: string | null;
  busy?: boolean;
}>;

/** Shared action IconButton styles (Assessment Center AI/Manual grids + Regulation Details history). */
export const AI_IMPACT_ACTION_BUTTON_SX = {
  padding: 0.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "text.secondary",
  "&:hover": {
    color: "primary.main",
    backgroundColor: "transparent",
    "& img": {
      opacity: 0.85,
    },
  },
  "& img": {
    opacity: 1,
  },
} as const;

const IMPACT_ACTION_ICON_IMG_SX = {
  width: 16,
  height: 16,
  display: "block",
} as const;

const IMPACT_ACTION_TOOLTIP_SLOT_PROPS = {
  popper: {
    sx: {
      zIndex: (theme: { zIndex: { modal: number } }) =>
        theme.zIndex.modal +
        ELEVATED_DIALOG_Z_INDEX_OFFSET +
        ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
    },
  },
} as const;

function ImpactAssessmentActionIconButton({
  ariaLabel,
  title,
  disabled,
  onClick,
  src,
  busy = false,
  elevatedDialogTooltips = false,
}: Readonly<{
  ariaLabel: string;
  title: string;
  disabled: boolean;
  onClick?: () => void;
  src: string;
  busy?: boolean;
  /** Raise tooltip above nested dialogs (AI Results modal). */
  elevatedDialogTooltips?: boolean;
}>) {
  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      disableInteractive
      slotProps={
        elevatedDialogTooltips ? IMPACT_ACTION_TOOLTIP_SLOT_PROPS : undefined
      }
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconButton
          size="small"
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={onClick}
          sx={AI_IMPACT_ACTION_BUTTON_SX}
        >
          {busy ? (
            <CircularProgress size={18} sx={{ color: "primary.main" }} />
          ) : (
            <Box component="img" src={src} alt="" sx={IMPACT_ACTION_ICON_IMG_SX} />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

/**
 * Shared AI Impact Assessment action strip (Assessment Center grid + Regulation Details history).
 */
export function AiImpactAssessmentActionIcons({
  row,
  handlers,
  elevatedDialogTooltips = false,
  contentAlign = "center",
}: Readonly<{
  row: AiImpactAssessmentRow;
  handlers: AiImpactAssessmentActionHandlers;
  /** Use elevated tooltip z-index when rendered inside AI Results modal. */
  elevatedDialogTooltips?: boolean;
  /** Vertical alignment within a tall grid cell. */
  contentAlign?: "center" | "top";
}>) {
  const hasReport = Boolean(row?.reportUrl);
  const disabledByBusy = Boolean(handlers.busy);
  const downloadingThis =
    Boolean(handlers.downloadingFilePath) &&
    handlers.downloadingFilePath === row?.reportUrl;

  const visibility = getAiImpactAssessmentActionsVisibility(row?.rawStatus);

  const isSystemAssessmentType =
    typeof row?.assessmentType === "string" &&
    row.assessmentType.trim().toLowerCase() === "system";
  const canShowSystemRerun = Boolean(handlers.canRerunSystem);
  const mayRerunThisScope = !isSystemAssessmentType || canShowSystemRerun;

  const showReportIcons = visibility.showReportActions;
  const showRetryIcon = visibility.showRetry && mayRerunThisScope;

  const tooltipState = {
    hasReport,
    disabledByBusy,
    downloadingThis,
    onViewReport: Boolean(handlers.onViewReport),
    onDownloadReport: Boolean(handlers.onDownloadReport),
    onStartManualAssessment: Boolean(handlers.onStartManualAssessment),
    onRetry: Boolean(handlers.onRetry),
  };

  const renderActionIcon = (
    src: string,
    ariaLabel: string,
    title: string,
    disabled: boolean,
    onClick?: () => void,
    busyIndicator?: boolean,
  ) => (
    <ImpactAssessmentActionIconButton
      src={src}
      ariaLabel={ariaLabel}
      title={title}
      disabled={disabled}
      onClick={onClick}
      busy={busyIndicator}
      elevatedDialogTooltips={elevatedDialogTooltips}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: contentAlign === "top" ? "flex-start" : "center",
        justifyContent: contentAlign === "top" ? "flex-start" : "center",
        gap: 0.5,
        width: contentAlign === "top" ? "auto" : "100%",
        height: contentAlign === "top" ? "auto" : "100%",
      }}
    >
      {showReportIcons && (
        <>
          {renderActionIcon(
            eyeIcon,
            "view report",
            getAiImpactViewReportTooltip(tooltipState),
            !hasReport || !handlers.onViewReport || disabledByBusy,
            () => handlers.onViewReport?.(row),
          )}
          {renderActionIcon(
            downloadIcon,
            "download",
            getAiImpactDownloadReportTooltip(tooltipState),
            !hasReport || !handlers.onDownloadReport || disabledByBusy,
            () => handlers.onDownloadReport?.(row),
            downloadingThis,
          )}
          {handlers.onStartManualAssessment ? (
            renderActionIcon(
              gitBranchIcon,
              "Start Manual Assessment",
              getAiImpactStartManualAssessmentTooltip(tooltipState),
              disabledByBusy,
              () => handlers.onStartManualAssessment?.(row),
            )
          ) : null}
        </>
      )}

      {showRetryIcon && (
        <>
          {renderActionIcon(
            retryIcon,
            "re-run assessment",
            getAiImpactRerunTooltip(tooltipState),
            !handlers.onRetry || disabledByBusy,
            () => handlers.onRetry?.(row),
          )}
        </>
      )}
    </Box>
  );
}

export function createAiImpactAssessmentColumns(
  handlers: AiImpactAssessmentActionHandlers = {},
): GridColDef[] {
  return [
  {
    field: "regulationId",
    headerName: "Regulation ID",
    flex: 0.85,
    minWidth: 130,
    renderCell: (params: GridRenderCellParams<AiImpactAssessmentRow>) => (
      <RegulationIdLinkCell
        regulationId={params.row.regulationId}
        version={params.row.version}
      />
    ),
  },
  {
    field: "regulation",
    headerName: "Regulation Title",
    flex: 1.35,
    minWidth: 200,
    sortable: false,
  },
  {
    field: "version",
    headerName: "Version",
    flex: 0.65,
    minWidth: 120,
  },
  {
    field: "executedDate",
    headerName: "Executed Date",
    flex: 0.85,
    minWidth: 150,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.65,
    minWidth: 110,
    sortable: false,
    renderCell: (params: GridRenderCellParams<AiImpactAssessmentRow>) => {
      const row = params.row;
      const tooltipTitle = resolveAiAssessmentCenterStatusHoverTitle(
        row?.rawStatus,
        row?.status,
        row?.failedConceptNames,
      );
      return (
        <AiStyleAssessmentStatusCell
          rawStatus={resolveAssessmentStatusForPill(row?.rawStatus, row?.status)}
          tooltipTitle={tooltipTitle}
        />
      );
    },
  },
  {
    field: "executedBy",
    headerName: "Executed By",
    flex: 0.75,
    minWidth: 130,
  },
  {
    field: "assessmentType",
    headerName: "Assessment Type",
    flex: 0.85,
    minWidth: 140,
    valueFormatter: (value: string) => formatAiAssessmentTypeForDisplay(value),
  },
  {
    field: "impactedConcepts",
    headerName: "Impacted Values",
    flex: 1,
    minWidth: 160,
    sortable: false,
    cellClassName: "impacted-concepts-cell",
    renderCell: (params: GridRenderCellParams<AiImpactAssessmentRow>) => (
      <AiImpactedConceptsCell value={params.value} />
    ),
  },
  {
    field: "impactLevel",
    headerName: "Impact Level",
    flex: 0.6,
    minWidth: 120,
    renderCell: (params: GridRenderCellParams<AiImpactAssessmentRow>) => {
      const { backgroundColor, color, label } = getImpactLevelPillStyles(
        params.value,
      );

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              minWidth: 68,
              height: 24,
              px: "10px",
              py: "4px",
              borderRadius: "9999px",
              backgroundColor,
              color,
              ...IMPACT_LEVEL_PILL_FONT,
            }}
          >
            {label}
          </Box>
        </Box>
      );
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    filterable: false,
    // Partial status shows four actions (view, download, manual, re-run).
    minWidth: 176,
    maxWidth: 200,
    flex: 0.75,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<AiImpactAssessmentRow>) => (
      <AiImpactAssessmentActionIcons row={params.row} handlers={handlers} />
    ),
  },
  ];
}

export const aiImpactAssessmentColumns: GridColDef[] =
  createAiImpactAssessmentColumns();

export type ManualImpactAssessmentActionHandlers = Readonly<{
  canManageProcess?: boolean;
  canTerminate?: boolean;
  terminatingRowId?: string | null;
  downloadingFilePath?: string | null;
  onRequestTerminate?: (row: ManualImpactAssessmentGridRow) => void;
  onRequestView?: (row: ManualImpactAssessmentGridRow) => void;
  onRequestDownload?: (row: ManualImpactAssessmentGridRow) => void;
  onRequestShare?: (row: ManualImpactAssessmentGridRow) => void;
}>;

export function createManualImpactAssessmentColumns(
  handlers: ManualImpactAssessmentActionHandlers = {},
): GridColDef<ManualImpactAssessmentGridRow>[] {
  const {
    canManageProcess = false,
    canTerminate,
    terminatingRowId,
    downloadingFilePath,
    onRequestTerminate,
    onRequestView,
    onRequestDownload,
    onRequestShare,
  } = handlers;

  return [
    {
      field: "regulationId",
      headerName: "Regulation ID",
      flex: 0.85,
      minWidth: 130,
      renderCell: (
        params: GridRenderCellParams<ManualImpactAssessmentGridRow>,
      ) => (
        <RegulationIdLinkCell
          regulationId={params.row.regulationId}
          version={params.row.version}
        />
      ),
    },
    {
      field: "regulationTitle",
      headerName: "Regulation Title",
      flex: 1.35,
      minWidth: 200,
      sortable: false,
    },
    { field: "version", headerName: "Version", flex: 0.5, minWidth: 100 },
    {
      field: "processName",
      headerName: "Process Name",
      flex: 1,
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      renderCell: (
        params: GridRenderCellParams<ManualImpactAssessmentGridRow>,
      ) => (
        <ManualAssessmentProcessNameCell
          processName={String(params.value ?? params.row.processName ?? "—")}
          workflowUuid={params.row.workflowUuid ?? params.row.id ?? ""}
          processAssociationId={params.row.processAssociationId ?? ""}
          status={params.row.status ?? ""}
          canManageManualAssessment={canManageProcess}
          detailsWholeWorkflow={{
            regulationId: params.row.regulationId ?? "",
            version: params.row.version ?? "",
          }}
        />
      ),
    },
    {
      field: "initiatedBy",
      headerName: "Initiated By",
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: "initiatedDate",
      headerName: "Initiated Date",
      flex: 0.85,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.65,
      minWidth: 110,
      renderCell: (
        params: GridRenderCellParams<ManualImpactAssessmentGridRow>,
      ) => <ManualStyleAssessmentStatusCell rawStatus={params.row?.status} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 180,
      maxWidth: 260,
      flex: 0.55,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<ManualImpactAssessmentGridRow>) => {
        const row = params.row;
        const eligible =
          isManualWorkflowTerminateEligibleStatus(row.status) &&
          typeof row.workflowDbId === "number";
        const hasPdf = Boolean(row.attachmentS3Uri);
        const canView = hasPdf && Boolean(onRequestView);
        const canDownload = hasPdf && Boolean(onRequestDownload);
        const canShare = hasPdf && Boolean(onRequestShare);
        const showTerminate = Boolean(canTerminate && eligible);

        if (!canView && !canDownload && !canShare && !showTerminate) {
          const emptyTitle = !canTerminate
            ? "No permission"
            : "This process cannot be terminated";
          return (
            <Tooltip title={emptyTitle}>
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                  color: "text.disabled",
                  fontSize: "0.875rem",
                }}
              >
                —
              </Box>
            </Tooltip>
          );
        }

        const busy = terminatingRowId === row.id;
        const downloading = downloadingFilePath === row.attachmentS3Uri;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              width: "100%",
              height: "100%",
            }}
          >
            {canView ? (
              <ImpactAssessmentActionIconButton
                ariaLabel="View"
                title="View"
                disabled={busy || downloading}
                onClick={() => onRequestView?.(row)}
                src={eyeIcon}
              />
            ) : null}
            {canDownload ? (
              <ImpactAssessmentActionIconButton
                ariaLabel="Download"
                title={downloading ? "Downloading…" : "Download"}
                disabled={busy || downloading}
                onClick={() => onRequestDownload?.(row)}
                src={downloadIcon}
                busy={downloading}
              />
            ) : null}
            {canShare ? (
              <ImpactAssessmentActionIconButton
                ariaLabel="Share"
                title="Share"
                disabled={busy || downloading}
                onClick={() => onRequestShare?.(row)}
                src={shareIcon}
              />
            ) : null}
            {showTerminate ? (
              <ImpactAssessmentActionIconButton
                ariaLabel="Terminate"
                title={busy ? "Terminating…" : "Terminate"}
                disabled={busy}
                onClick={() => onRequestTerminate?.(row)}
                src={stopIcon}
                busy={busy}
              />
            ) : null}
          </Box>
        );
      },
    },
  ];
}

export type AiResultsModalRow = {
  id: string;
  regulationId: string;
  regulation: string;
  version: string;
  executedDate: string;
  /** Raw API / bundle status — actions visibility + status chip. */
  rawStatus: string;
  /** Formatted status label (optional; chip uses rawStatus). */
  status: string;
  executedBy: string;
  /** API `by` or scope label (`system`, `user`, Company Scope, …). */
  assessmentType: string;
  impactedConcepts: string;
  impactLevel: string;
  taskUuid?: string;
  reportUrl?: string | null;
  failedConceptNames?: readonly string[];
  impactedItems?: readonly TriageImpactedItem[];
};

export type AiResultsModalActionHandlers = Omit<
  AiImpactAssessmentActionHandlers,
  "onStartManualAssessment"
> &
  Readonly<{
    /** Closes the AI Results modal before opening Manual Assessment (same as Close). */
    onCloseModal?: () => void;
    onStartManualAssessment?: (row: AiResultsModalRow) => void;
  }>;

export function mapAiResultsModalRowToActionRow(
  row: AiResultsModalRow,
): AiImpactAssessmentRow {
  return {
    rawStatus: row.rawStatus || row.status,
    status: row.status,
    taskUuid: row.taskUuid ?? "",
    regulationId: row.regulationId,
    regulation: row.regulation,
    version: row.version,
    assessmentType: row.assessmentType,
    reportUrl: row.reportUrl,
    failedConceptNames: row.failedConceptNames,
    impactedItems: row.impactedItems,
  };
}

function getModalActionColumns(
  handlers: AiResultsModalActionHandlers,
): GridColDef {
  const { onStartManualAssessment, onCloseModal, ...actionHandlers } = handlers;

  return {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    filterable: false,
    minWidth: 176,
    maxWidth: 200,
    flex: 0.75,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<AiResultsModalRow>) => (
      <AiImpactAssessmentActionIcons
        row={mapAiResultsModalRowToActionRow(params.row)}
        elevatedDialogTooltips
        handlers={{
          ...actionHandlers,
          onStartManualAssessment: onStartManualAssessment
            ? () => {
                onCloseModal?.();
                onStartManualAssessment(params.row);
              }
            : undefined,
        }}
      />
    ),
  };
}

export function createAiResultsModalColumns(
  handlers: AiResultsModalActionHandlers = {},
): GridColDef[] {
  return [
    {
      field: "regulationId",
      headerName: "Regulation ID",
      flex: 0.85,
      minWidth: 130,
    },
    {
      field: "regulation",
      headerName: "Regulation Title",
      flex: 1.35,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "version",
      headerName: "Version",
      flex: 0.65,
      minWidth: 120,
    },
    {
      field: "executedDate",
      headerName: "Executed Date",
      flex: 0.85,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.65,
      minWidth: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AiResultsModalRow>) => {
        const row = params.row;
        const tooltipTitle = resolveAiAssessmentCenterStatusHoverTitle(
          row?.rawStatus,
          row?.status,
          row?.failedConceptNames,
        );
        return (
          <AiStyleAssessmentStatusCell
            rawStatus={resolveAssessmentStatusForPill(row?.rawStatus, row?.status)}
            tooltipTitle={tooltipTitle}
            elevatedDialogTooltips
          />
        );
      },
    },
    {
      field: "executedBy",
      headerName: "Executed By",
      flex: 0.75,
      minWidth: 130,
    },
    {
      field: "assessmentType",
      headerName: "Assessment Type",
      flex: 0.85,
      minWidth: 140,
      valueFormatter: (value: string) => formatAiAssessmentTypeForDisplay(value),
    },
    {
      field: "impactedConcepts",
      headerName: "Impacted Values",
      flex: 1,
      minWidth: 160,
      sortable: false,
      cellClassName: "impacted-concepts-cell",
      renderCell: (params: GridRenderCellParams<AiResultsModalRow>) => (
        <AiImpactedConceptsCell value={params.value} />
      ),
    },
    {
      field: "impactLevel",
      headerName: "Impact Level",
      flex: 0.6,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<AiResultsModalRow>) => {
        const { backgroundColor, color, label } = getImpactLevelPillStyles(
          params.value,
        );
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                minWidth: 68,
                height: 24,
                px: "10px",
                py: "4px",
                borderRadius: "9999px",
                backgroundColor,
                color,
                ...IMPACT_LEVEL_PILL_FONT,
              }}
            >
              {label}
            </Box>
          </Box>
        );
      },
    },
    getModalActionColumns(handlers),
  ];
}
